import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { X, Mail, MessageCircle, AlertCircle, ArrowLeft, Lock, Eye, EyeOff } from 'lucide-react';
import { fetchCaptcha, verifyCaptchaThunk, verifyPasswordLoginThunk, clearError } from '../store/userSlice';
import { useTranslation } from 'react-i18next';

export default function LoginModal({ isOpen, onClose, onLogin }) {
  const dispatch = useDispatch();
  const { loading: reduxLoading, error: reduxError } = useSelector(state => state.user);
  const { t } = useTranslation();
  
  // 登录方式：'code' | 'password'
  const [loginMode, setLoginMode] = useState('code');
  // 屏幕状态：'email' | 'verification' (仅验证码模式使用)
  const [currentScreen, setCurrentScreen] = useState('email');
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    verificationCode: ['', '', '', '', '', ''] // 改为数组存储6位验证码
  });
  const [showPassword, setShowPassword] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [localError, setLocalError] = useState('');
  const [success, setSuccess] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(null);

  const loading = reduxLoading;

  // 处理表单输入
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // 清除错误信息
    if (localError) setLocalError('');
    if (reduxError) dispatch(clearError());
  };

  // 处理验证码输入
  const handleCodeChange = (index, value) => {
    // 只允许数字
    if (!/^\d*$/.test(value)) return;
    
    const newCode = [...formData.verificationCode];
    newCode[index] = value;
    
    setFormData(prev => ({
      ...prev,
      verificationCode: newCode
    }));
    
    // 清除错误信息
    if (localError) setLocalError('');
    if (reduxError) dispatch(clearError());
    
    // 自动跳转到下一个输入框
    if (value && index < 5) {
      const nextInput = document.getElementById(`code-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  // 处理退格键
  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !formData.verificationCode[index] && index > 0) {
      const prevInput = document.getElementById(`code-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  // 验证邮箱格式
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // 处理邮箱提交（第一屏）
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !isValidEmail(formData.email)) {
      setLocalError(t('loginModal.errors.invalidEmail'));
      return;
    }

    setLocalError('');
    dispatch(clearError());
    
    try {
      // 使用Redux thunk发送验证码
      const result = await dispatch(fetchCaptcha(formData.email));
      
      if (fetchCaptcha.fulfilled.match(result)) {
        setCodeSent(true);
        setCountdown(60);
        setSuccess(t('loginModal.success.codeSent'));
        
        // 切换到验证码屏幕
        setCurrentScreen('verification');
        
        // 倒计时
        const timer = setInterval(() => {
          setCountdown(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              setCodeSent(false);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        // 处理发送失败的情况
        setLocalError(result.payload || t('loginModal.errors.sendCodeFailed'));
      }
      
    } catch (err) {
      setLocalError(err.message || t('loginModal.errors.sendCodeFailed'));
    }
  };

  // 重新发送验证码
  const handleResendCode = async () => {
    setLocalError('');
    dispatch(clearError());
    
    try {
      // 使用Redux thunk重新发送验证码
      const result = await dispatch(fetchCaptcha(formData.email));
      
      if (fetchCaptcha.fulfilled.match(result)) {
        setCodeSent(true);
        setCountdown(60);
        setSuccess(t('loginModal.success.codeResent'));
        
        // 倒计时
        const timer = setInterval(() => {
          setCountdown(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              setCodeSent(false);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setLocalError(result.payload || t('loginModal.errors.sendCodeFailed'));
      }
      
    } catch (err) {
      setLocalError(err.message || t('loginModal.errors.sendCodeFailed'));
    }
  };

  // 处理验证码提交（第二屏）
  const handleVerificationSubmit = async (e) => {
    e.preventDefault();
    
    const code = formData.verificationCode.join('');
    
    if (!code || code.length !== 6) {
      setLocalError(t('loginModal.errors.incompleteCode'));
      return;
    }

    setLocalError('');
    dispatch(clearError());

    try {
      // 使用Redux thunk验证验证码
      const result = await dispatch(verifyCaptchaThunk({
        phoneNumbers: formData.email,
        smsCode: code
      }));
      
      if (verifyCaptchaThunk.fulfilled.match(result)) {
        setSuccess(t('loginModal.success.loginSuccess'));
        
        // 调用父组件的登录回调（不需要传递用户数据，Redux已管理）
        if (onLogin) {
          onLogin();
        }
        
        // 延迟关闭弹窗
        setTimeout(() => {
          handleClose();
        }, 1000);
      } else {
        setLocalError(result.payload || t('loginModal.errors.verificationFailed'));
      }
      
    } catch (err) {
      setLocalError(err.message || t('loginModal.errors.loginFailed'));
    }
  };

  // 处理账号密码登录提交
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email) {
      setLocalError(t('loginModal.errors.emailRequired'));
      return;
    }

    if (!formData.password) {
      setLocalError(t('loginModal.errors.passwordRequired'));
      return;
    }

    setLocalError('');
    dispatch(clearError());

    try {
      // 调用账号密码登录API
      const result = await dispatch(verifyPasswordLoginThunk({
        phoneNumbers: formData.email,
        password: formData.password
      }));

      if (result.type === 'user/verifyPasswordLogin/fulfilled') {
        // 登录成功
        setSuccess(t('loginModal.success.loginSuccess'));
        
        if (onLogin) {
          onLogin();
        }
        
        setTimeout(() => {
          handleClose();
        }, 1000);
      } else {
        // 登录失败
        setLocalError(result.payload || t('loginModal.errors.loginFailed'));
      }
    } catch (err) {
      setLocalError(err.message || t('loginModal.errors.loginFailed'));
    }
  };

  // 返回邮箱输入屏幕
  const handleBackToEmail = () => {
    setCurrentScreen('email');
    setLocalError('');
    setSuccess('');
    dispatch(clearError());
    setFormData(prev => ({ ...prev, verificationCode: ['', '', '', '', '', ''] }));
  };

  // 切换登录模式
  const handleModeSwitch = (mode) => {
    setLoginMode(mode);
    setCurrentScreen('email');
    setLocalError('');
    setSuccess('');
    setShowPassword(false);
    dispatch(clearError());
    setFormData({
      email: '',
      password: '',
      verificationCode: ['', '', '', '', '', '']
    });
  };

  // 关闭弹窗并重置状态
  const handleClose = () => {
    onClose();
    setLoginMode('code');
    setCurrentScreen('email');
    setFormData({
      email: '',
      password: '',
      verificationCode: ['', '', '', '', '', '']
    });
    setCodeSent(false);
    setCountdown(0);
    setLocalError('');
    setSuccess('');
    setShowPassword(false);
    dispatch(clearError());
  };

  // 获取当前错误信息（优先显示本地错误）
  const currentError = localError || reduxError;

  // 如果弹窗关闭，不渲染
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* 背景遮罩 */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* 弹窗内容 */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden transform transition-all duration-300">
        
        {/* 头部装饰 */}
        <div className="bg-gradient-to-r from-purple-500 to-amber-500 p-6 text-white relative">
          {/* 返回按钮（仅在验证码屏幕显示） */}
          {loginMode === 'code' && currentScreen === 'verification' && (
            <button
              onClick={handleBackToEmail}
              className="absolute top-4 left-4 w-8 h-8 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 flex items-center justify-center transition-all duration-300"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 flex items-center justify-center transition-all duration-300"
          >
            <X className="w-4 h-4" />
          </button>
          
          <div className="text-center">
            <div className="flex justify-center mx-auto mb-3">
              <img
                src="/images/logo192.png"
                alt="紫刀AI"
                className="w-14 h-14 object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div className="w-14 h-14 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center hidden">
                {loginMode === 'password' ? (
                  <Lock className="w-8 h-8" />
                ) : currentScreen === 'email' ? (
                  <Mail className="w-8 h-8" />
                ) : (
                  <MessageCircle className="w-8 h-8" />
                )}
              </div>
            </div>
            {/* 品牌名称 */}
            <div className="text-xl font-black tracking-widest text-white mb-1">
              {t('loginModal.brandName')}
            </div>
            <h2 className="text-base font-semibold text-purple-100">
              {loginMode === 'code' && currentScreen === 'verification'
                ? t('loginModal.title.verification')
                : t('loginModal.title.login')}
            </h2>
            <p className="text-purple-200 text-xs mt-1">
              {loginMode === 'code' && currentScreen === 'verification'
                ? t('loginModal.subtitle.verification', {
                    email: formData.email.replace(/(.{3}).*(@.*)/, '$1***$2')
                  })
                : loginMode === 'password'
                ? t('loginModal.subtitle.passwordLogin')
                : t('loginModal.subtitle.login')
              }
            </p>
          </div>
        </div>


        {/* 表单内容 */}
        <div className="p-8">
          {/* 错误/成功信息 */}
          {currentError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">{currentError}</span>
            </div>
          )}
          
          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
              <MessageCircle className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">{success}</span>
            </div>
          )}

          {/* 账号密码登录屏幕 */}
          {loginMode === 'password' && (
            <>
              <form onSubmit={handlePasswordSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('loginModal.form.passwordEmailLabel')}
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder={t('loginModal.form.passwordEmailPlaceholder')}
                      className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none transition-colors duration-300"
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('loginModal.form.passwordLabel')}
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder={t('loginModal.form.passwordPlaceholder')}
                      className="w-full pl-12 pr-12 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none transition-colors duration-300"
                      required
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-300"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-purple-500 to-amber-500 text-white py-4 rounded-xl font-semibold text-lg hover:from-purple-600 hover:to-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    t('loginModal.form.login')
                  )}
                </button>
              </form>

              {/* 切换到验证码登录 */}
              <div className="text-center mt-5">
                <button
                  onClick={() => handleModeSwitch('code')}
                  className="bg-gradient-to-r from-purple-300 to-amber-300 text-white text-xs px-6 py-2 rounded-lg font-medium hover:from-purple-400 hover:to-amber-400 transition-all duration-200"
                >
                  {t('loginModal.switch.toCode')}
                </button>
              </div>
            </>
          )}

          {/* 邮箱输入屏幕（验证码模式） */}
          {loginMode === 'code' && currentScreen === 'email' && (
            <>
              <form onSubmit={handleEmailSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('loginModal.form.emailLabel')}
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder={t('loginModal.form.emailPlaceholder')}
                      className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none transition-colors duration-300"
                      required
                      disabled={loading}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {t('loginModal.form.emailHint')}
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-purple-500 to-amber-500 text-white py-4 rounded-xl font-semibold text-lg hover:from-purple-600 hover:to-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    t('loginModal.form.sendCode')
                  )}
                </button>
              </form>

              {/* 切换到账号密码登录 */}
              <div className="text-center mt-5">
                <button
                  onClick={() => handleModeSwitch('password')}
                  className="bg-gradient-to-r from-purple-300 to-amber-300 text-white text-xs px-6 py-2 rounded-lg font-medium hover:from-purple-400 hover:to-amber-400 transition-all duration-200"
                >
                  {t('loginModal.switch.toPassword')}
                </button>
              </div>
            </>
          )}

          {/* 验证码输入屏幕（验证码模式） */}
          {loginMode === 'code' && currentScreen === 'verification' && (
            <>
              <form onSubmit={handleVerificationSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-4 text-center">
                    {t('loginModal.form.verificationLabel')}
                  </label>
                  
                  {/* 6个验证码输入框 */}
                  <div className="flex justify-center gap-3 mb-6">
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                      <input
                        key={index}
                        id={`code-${index}`}
                        type="text"
                        maxLength="1"
                        value={formData.verificationCode[index]}
                        onChange={(e) => handleCodeChange(index, e.target.value)}
                        onKeyDown={(e) => handleCodeKeyDown(index, e)}
                        className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none transition-colors duration-300"
                        disabled={loading}
                        autoFocus={index === 0}
                      />
                    ))}
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <p className="text-sm text-gray-500">
                      {t('loginModal.form.noCode')}
                    </p>
                    <button
                      type="button"
                      onClick={handleResendCode}
                      disabled={loading || codeSent}
                      className="text-sm text-purple-500 hover:text-purple-600 disabled:text-gray-400 disabled:cursor-not-allowed font-medium"
                    >
                      {codeSent ? t('loginModal.form.resendCountdown', { countdown }) : t('loginModal.form.resend')}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-purple-500 to-amber-500 text-white py-4 rounded-xl font-semibold text-lg hover:from-purple-600 hover:to-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    t('loginModal.form.login')
                  )}
                </button>
              </form>
            </>
          )}

          {/* 常见问题 */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-center gap-2 flex-wrap">
              {[
                { key: 0, q: t('loginModal.faq.phoneQuestion'), a: t('loginModal.faq.phoneAnswer') },
                { key: 1, q: t('loginModal.faq.codeQuestion'), a: t('loginModal.faq.codeAnswer') },
                { key: 2, q: t('loginModal.faq.passwordTip'), a: t('loginModal.faq.passwordTipDetail') }
              ].map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setExpandedFaq(expandedFaq === item.key ? null : item.key)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-all duration-200 ${
                    expandedFaq === item.key
                      ? 'border-purple-300 bg-purple-50 text-purple-600'
                      : 'border-gray-200 text-gray-500 hover:border-purple-200 hover:text-purple-500'
                  }`}
                >
                  {item.q}
                </button>
              ))}
            </div>
            {expandedFaq !== null && (
              <p className="text-xs text-gray-500 leading-relaxed text-center mt-3">
                {expandedFaq === 0 ? (
                  <>
                    {t('loginModal.faq.phoneAnswer').split('gpt2you.com/list')[0]}
                    <a
                      href="https://gpt2you.com/list"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-purple-500 underline hover:text-purple-700"
                    >
                      gpt2you.com/list
                    </a>
                    {t('loginModal.faq.phoneAnswer').split('gpt2you.com/list')[1]}
                  </>
                ) : [
                  t('loginModal.faq.phoneAnswer'),
                  t('loginModal.faq.codeAnswer'),
                  t('loginModal.faq.passwordTipDetail')
                ][expandedFaq]}
              </p>
            )}
          </div>

          {/* 登录说明 */}
          <div className="text-center mt-4 pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-500 leading-relaxed">
              {t('loginModal.footer.agreement')}
              <button className="text-purple-500 hover:text-purple-600 mx-1">{t('loginModal.footer.terms')}</button>
              {t('loginModal.footer.and')}
              <button className="text-purple-500 hover:text-purple-600 mx-1">{t('loginModal.footer.privacy')}</button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
} 